import React, {
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useId,
  useState
} from "react";
import Input from "@/components/ui/input";
import { useClipboard } from "@dreamhub/designsystem";
import TextareaAutosize from "react-textarea-autosize";
import Text, { TextVariant } from "@/components/ui/text";
import { variantClasses } from "@/components/ui/text-variants";
import { Add, Close } from "@dreamhub/designsystem/icons";
import { CircleDollarSign, Mail, Percent, Copy, Check } from "lucide-react";
import IconContainer from "@/components/ui/icon-container";
import { cn } from "@/utils/utils";
import { ValidationConfig } from "@/utils/validation";
import { isEmpty } from "lodash";
import { IconButton } from "@/components/ui/icon-button";
import { useInputWithValidation } from "@/hooks/useInputWithValidation";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";
import { useTranslation } from "react-i18next";

export interface SingleInputRef {
  startEditing: () => void;
  forceStartEditing: () => void;
}

export interface SingleInputProps {
  fieldName: string;
  initialValue?: string;
  onSave?: (value: string) => void;
  textVariant?: TextVariant;
  inputVariant?: TextVariant;
  validation?: ValidationConfig;
  isMonetary?: boolean;
  isNumericOnly?: boolean;
  maxDigits?: number;
  isPretty?: boolean;
  customEditButton?: JSX.Element;
  clearOnSave?: boolean;
  inputClassName?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  containerClassName?: string;
  editModeContainerClassName?: string;
  onError?: (error: string | null) => void;
  // Called when Escape abandons an edit.
  onCancel?: () => void;
  // Id of an error message the host renders itself (from onError): the input
  // shows none of its own and points at that one instead.
  externalErrorId?: string;
  currencySymbol?: string;
  onClickClearButton?: () => void;
  showClearButton?: boolean;
  useTextarea?: boolean;
  minRows?: number;
  placeholder?: string;
  width?: string;
  maxLength?: number;
  divClassName?: string;
  /**
   * Where the field sits, which decides its body. `"default"` is the
   * pre-redesign body and the safe default: a caller has to ask for a
   * redesigned placement to get one.
   */
  variant?: "default" | "topFields" | "inline";
  isPercentage?: boolean;
  isEmail?: boolean;
  /**
   * Clip the displayed value to one line with an ellipsis instead of wrapping,
   * revealing the full value on hover through `Text`'s `hasTruncatedTooltip`.
   * Opt-in: the caller also has to let this shrink — a flex ancestor needs
   * `min-w-0` or the value's own width still sets the floor.
   *
   * Applies to a populated value only: the edit input and the placeholder shown
   * for an empty value are both unaffected.
   */
  truncate?: boolean;
  /**
   * Let a blur save an empty value that was already empty. Off by default: a
   * field the user clicks into and leaves untouched should not issue a write.
   * MultiInput turns it on because saving empty is how it removes a row.
   */
  saveUnchangedEmpty?: boolean;
}

const SingleInput = forwardRef<SingleInputRef, SingleInputProps>(
  (
    {
      fieldName,
      initialValue = "",
      onSave = () => {},
      textVariant = "body",
      inputVariant = "body",
      validation,
      isMonetary = false,
      isNumericOnly = false,
      maxDigits,
      isPretty = false,
      customEditButton,
      clearOnSave = false,
      inputClassName,
      disabled = false,
      readOnly = false,
      className,
      containerClassName,
      editModeContainerClassName,
      onError,
      onCancel,
      externalErrorId,
      currencySymbol,
      onClickClearButton,
      showClearButton = true,
      useTextarea = false,
      minRows = 1,
      placeholder,
      width = "100%",
      maxLength,
      divClassName,
      variant = "default",
      isPercentage = false,
      isEmail = false,
      truncate = false,
      saveUnchangedEmpty = false
    },
    ref
  ) => {
    const isNewFieldsUI = useNewFieldsUI();
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState<boolean>(false);
    // Generated, not built from fieldName: rows like price cells share one.
    const ownErrorId = useId();
    const errorId = externalErrorId ?? ownErrorId;
    const inputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const {
      value,
      error,
      setError,
      setValue,
      handleChange: handleInputChange,
      handleBlur: handleInputBlur,
      getRawValue,
      getMonetaryDisplayValue
    } = useInputWithValidation({
      initialValue,
      validation,
      isMonetary,
      isNumericOnly,
      maxDigits,
      isPretty,
      currencySymbol,
      onSave: async (rawValue) => {
        setIsEditing(false);
        const initialRaw = isMonetary
          ? initialValue.replace(/,/g, "")
          : initialValue;
        // A blur that changed nothing saves nothing. MultiInput opts out: it
        // removes a row by saving it empty, so an untouched blank entry has to
        // reach onSave there or the phantom row it opened is never cleaned up.
        if (rawValue === initialRaw && !(saveUnchangedEmpty && rawValue === ""))
          return;
        try {
          await onSave(rawValue);
          if (clearOnSave) {
            setValue("");
          }
        } catch (error) {
          console.error("Error saving value:", error);
        }
      }
    });

    // navigator.clipboard is absent in a non-secure context, so reading
    // .writeText off it throws synchronously — before any .catch is attached.
    // useClipboard performs the access inside its own try/catch.
    const { hasCopied, onCopy } = useClipboard(value);

    useImperativeHandle(ref, () => ({
      startEditing: () => {
        if (!disabled) {
          setIsEditing(true);
        }
      },
      forceStartEditing: () => {
        setIsEditing(true);
      }
    }));

    useEffect(() => {
      if (isEditing) {
        if (useTextarea && textareaRef.current) {
          textareaRef.current.focus();
          // Set cursor to end of text
          const length = textareaRef.current.value.length;
          textareaRef.current.setSelectionRange(length, length);
        } else if (inputRef.current) {
          inputRef.current.focus();
          // Set cursor to end of text
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
        }
      }
    }, [isEditing, useTextarea]);

    useEffect(() => {
      onError?.(error);
    }, [error, onError]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleInputBlur();
      } else if (e.key === "Escape") {
        setIsEditing(false);
        setValue(initialValue);
        setError(null);
        onCancel?.();
      }
    };

    const handleTextareaKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleInputBlur();
        } else if (e.key === "Escape") {
          setIsEditing(false);
          setValue(initialValue);
          setError(null);
          onCancel?.();
        }
      },
      [handleInputBlur, initialValue, setValue, setError, onCancel]
    );

    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void => {
      handleInputChange(e.target.value);
    };

    const handleEditClick = () => {
      if (!disabled) {
        setIsEditing(true);
      }
    };

    // The redesigned rows stay divs because they host the copy button and the
    // field icons, and a <button> may not nest those. They still have to answer
    // the keyboard like the control they present as. Omitted while disabled, so
    // a locked row is not in the tab order.
    const editRowKeyboardProps = disabled
      ? {}
      : {
          // Not role="button": that makes descendants presentational, and the
          // row hosts real buttons (the isEmail copy control, Text's own
          // copyable IconButton) which would leave the accessibility tree
          // entirely. But a div with no role has the ARIA `generic` role, and
          // ARIA 1.2 prohibits naming `generic` — so the aria-label the callers
          // add was free to be ignored, leaving a focusable row announced with
          // neither name nor role. `group` is nameable and keeps descendant
          // buttons exposed, which is exactly the pair of properties needed.
          // Making the edit target and the copy control siblings is still the
          // fuller fix, and still a layout change not done here.
          role: "group",
          tabIndex: 0,
          onKeyDown: (keyEvent: React.KeyboardEvent) => {
            // The row hosts real buttons (the copy control). Their Enter and
            // Space belong to them — answering a bubbled keypress here would
            // preventDefault the button's own activation away and open the
            // editor instead. Same guard ClickableUrlField and MultiSelect use.
            if (keyEvent.target !== keyEvent.currentTarget) return;
            if (keyEvent.defaultPrevented) return;
            if (keyEvent.key !== "Enter" && keyEvent.key !== " ") return;
            keyEvent.preventDefault();
            handleEditClick();
          }
        };

    const handleClearClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onClickClearButton) {
        onClickClearButton();
      } else {
        setValue("");
      }
    };

    const renderCustomEditButton = (
      keyboardProps: React.HTMLAttributes<HTMLDivElement> = {}
    ) => (
      <div
        onClick={handleEditClick}
        {...keyboardProps}
        className={cn("cursor-pointer", disabled && "cursor-default")}
      >
        {customEditButton}
      </div>
    );

    const renderLegacyEditButton = () => {
      if (customEditButton) {
        return renderCustomEditButton();
      }

      if (isEmpty(value) && placeholder) {
        return (
          <div
            onClick={handleEditClick}
            className={cn(
              "hover:bg-surface-hover-strong rounded-lg cursor-pointer w-full p-2 min-h-9 flex items-center",
              disabled && "hover:!bg-transparent cursor-default !opacity-100",
              divClassName
            )}
            aria-label={t("clickToEdit")}
          >
            <Text variant={textVariant} className="!text-tertiary">
              {placeholder}
            </Text>
          </div>
        );
      }

      return (
        <div
          data-test={`${fieldName}-new-text-button`}
          onClick={handleEditClick}
          className={cn(
            "hover:bg-surface-hover-strong rounded-lg cursor-text w-full p-2 min-h-9 flex items-center",
            isEmpty(value) && "cursor-pointer",
            disabled && "hover:!bg-transparent cursor-default !opacity-100",
            truncate && "min-w-0",
            divClassName
          )}
          aria-label={t("clickToEdit")}
        >
          {isEmpty(value) ? (
            <span className="opacity-0 inline-flex items-center group-hover:opacity-100 transition-opacity duration-200">
              <Add />
            </span>
          ) : (
            // `min-w-0` on the span as well as the row: a flex item's
            // `min-width: auto` is what blocks shrinking, and `truncate` on the
            // text never clips while its box is free to grow to fit.
            <span className={cn("block", truncate && "min-w-0 flex-1")}>
              {/* `truncate` + `hasTruncatedTooltip` is how the rest of the app
                  clips a value and reveals it on hover (21 files, e.g.
                  Financials.tsx and CompanyFinancialsSection.tsx). Text measures
                  real overflow with a ResizeObserver, so the tooltip appears only
                  when the value is actually clipped — and it is the design
                  system's own tooltip rather than browser chrome. */}
              <Text
                variant={textVariant}
                copyable={
                  fieldName.includes("email") || fieldName.includes("Email")
                }
                copyLabels={{ tooltip: t("copyTooltip"), copied: t("copied") }}
                hasTruncatedTooltip={truncate}
                className={cn(
                  useTextarea && "whitespace-pre-wrap",
                  truncate && "truncate"
                )}
              >
                {getMonetaryDisplayValue(value)}
              </Text>
            </span>
          )}
        </div>
      );
    };

    const renderNewEditButton = () => {
      if (customEditButton) {
        // The other redesigned rows are keyboard-operable; this one routed
        // through the shared helper and was not. The legacy caller below still
        // passes nothing, so flag-off stays byte-identical to base.
        return renderCustomEditButton({
          ...editRowKeyboardProps,
          "aria-label": t("clickToEdit")
        });
      }

      if (isEmpty(value) && placeholder) {
        return (
          <div
            onClick={handleEditClick}
            {...editRowKeyboardProps}
            className={cn(
              "group/row rounded-lg w-full px-2 py-1 min-h-9 flex items-center justify-between",
              variant === "inline" &&
                "border-[0.5px] border-transparent transition-colors duration-200 ease-in-out",
              variant === "inline" &&
                !disabled &&
                "hover:bg-surface-hover-soft",
              // Cursor isn't variant-gated: it should match text-editor's hover cursor everywhere.
              !disabled && "cursor-text",
              disabled && "cursor-default",
              divClassName
            )}
            aria-label={t("clickToEdit")}
          >
            <Text
              variant={textVariant}
              className={cn(
                "!text-tertiary flex-1",
                disabled && "!text-body-disabled"
              )}
            >
              {placeholder}
            </Text>
            {isMonetary && !disabled && (
              <IconContainer
                size="5"
                color="tertiary"
                className="flex-shrink-0 transition-all duration-150 opacity-0 group-hover/row:opacity-100"
              >
                <CircleDollarSign size={20} strokeWidth={1.5} />
              </IconContainer>
            )}
            {isPercentage && !disabled && (
              <IconContainer
                size="5"
                color="tertiary"
                className="flex-shrink-0 transition-all duration-150 opacity-0 group-hover/row:opacity-100"
              >
                <Percent size={20} strokeWidth={1.5} />
              </IconContainer>
            )}
            {isEmail && !disabled && (
              <IconContainer
                size="5"
                color="tertiary"
                className="flex-shrink-0 transition-all duration-150 opacity-0 group-hover/row:opacity-100"
              >
                <Mail size={20} strokeWidth={1.5} />
              </IconContainer>
            )}
          </div>
        );
      }

      return (
        <div
          data-test={`${fieldName}-new-text-button`}
          onClick={handleEditClick}
          {...editRowKeyboardProps}
          className={cn(
            "group/row rounded-lg w-full pl-2 pr-[6px] py-1 min-h-9 flex items-center",
            variant === "inline" &&
              "border-[0.5px] border-transparent transition-colors duration-200 ease-in-out",
            variant === "inline" && !disabled && "hover:bg-surface-hover-soft",
            // Cursor isn't variant-gated like the hover bg above: it should match
            // text-editor's hover cursor everywhere.
            !disabled && (isEmpty(value) ? "cursor-pointer" : "cursor-text"),
            disabled && "cursor-default",
            divClassName
          )}
          aria-label={t("clickToEdit")}
        >
          {isEmpty(value) ? (
            variant !== "inline" &&
            !disabled && (
              <span className="opacity-0 inline-flex items-center group-hover/row:opacity-100 transition-opacity duration-200">
                <Add />
              </span>
            )
          ) : (
            <>
              <span className="block flex-1 min-w-0">
                <Text
                  variant={textVariant}
                  copyable={
                    !disabled &&
                    !isEmail &&
                    (fieldName.includes("email") || fieldName.includes("Email"))
                  }
                  copyLabels={{
                    tooltip: t("copyTooltip"),
                    copied: t("copied")
                  }}
                  copyVariant="inline"
                  className={cn(
                    // A textarea value wraps, so it has nothing to truncate;
                    // every other value is one line and must not widen the row.
                    useTextarea ? "whitespace-pre-wrap" : "truncate",
                    disabled && "!text-body-disabled"
                  )}
                >
                  {isPercentage
                    ? `${getMonetaryDisplayValue(value)} %`
                    : getMonetaryDisplayValue(value)}
                </Text>
              </span>
              {isEmail && !disabled && (
                <IconButton
                  size="md"
                  variant="icon"
                  iconColor="secondary"
                  type="button"
                  aria-label={hasCopied ? t("copied") : t("copyTooltip")}
                  tooltipText={hasCopied ? t("copied") : t("copyTooltip")}
                  className="opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 transition-opacity flex-shrink-0 ml-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    void onCopy();
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {hasCopied ? <Check /> : <Copy />}
                </IconButton>
              )}
              {isMonetary && !disabled && (
                <IconContainer
                  size="5"
                  color="tertiary"
                  className="flex-shrink-0 transition-all duration-150 opacity-0 group-hover/row:opacity-100"
                >
                  <CircleDollarSign size={20} strokeWidth={1.5} />
                </IconContainer>
              )}
              {isPercentage && !disabled && (
                <IconContainer
                  size="5"
                  color="tertiary"
                  className="flex-shrink-0 transition-all duration-150 opacity-0 group-hover/row:opacity-100"
                >
                  <Percent size={20} strokeWidth={1.5} />
                </IconContainer>
              )}
            </>
          )}
        </div>
      );
    };

    // `"default"` opts a call site out of the redesign even with the flag on,
    // the way SingleSelect's own `"default"` does. The redesign was drawn for
    // detail rows and the header strip; the fourteen call sites that predate it
    // — view names, quick-view titles, price cells, commercial panels — are
    // neither, and silently inheriting a placement they were never designed for
    // is what made the default dangerous. They keep the body they have today.
    // One value, because `variant="default"` is an opt-out from the whole
    // redesigned body — not just its display row. Testing `isNewFieldsUI` alone
    // in the edit container and the error text gave the fourteen pre-redesign
    // call sites a legacy display row and a redesigned edit row, mixing two
    // looks in one control.
    const usesRedesignedBody = isNewFieldsUI && variant !== "default";

    const renderEditButton = () =>
      usesRedesignedBody ? renderNewEditButton() : renderLegacyEditButton();

    // Read-only is a mode rather than a look: only the redesigned field rows
    // ask for it, and there is no edit affordance to fall back to. The value is
    // formatted the same way the display row formats it.
    if (readOnly) {
      return (
        <div className="px-2 py-2 min-h-9 flex items-center">
          <Text variant={isEmpty(value) ? "bodyTertiary" : "body"}>
            {isEmpty(value)
              ? placeholder
              : isPercentage
                ? `${getMonetaryDisplayValue(value)} %`
                : getMonetaryDisplayValue(value)}
          </Text>
        </div>
      );
    }

    return (
      <div
        className={cn(
          "flex flex-col gap-1",
          isEmpty(value) && "items-start",
          className
        )}
        style={width ? { width } : undefined}
      >
        {isEditing ? (
          <div
            className={cn(
              usesRedesignedBody
                ? cn(
                    "flex items-center gap-1 w-full transition-colors duration-200 ease-in-out",
                    variant === "inline"
                      ? "h-9 rounded-lg bg-surface-focus-selected"
                      : "bg-surface-focus-selected"
                  )
                : "flex items-center gap-1 bg-white-4 w-full",
              editModeContainerClassName
            )}
          >
            {useTextarea ? (
              <TextareaAutosize
                ref={textareaRef}
                data-test={`${fieldName}-textarea`}
                value={getRawValue(value)}
                onChange={handleChange}
                onKeyDown={handleTextareaKeyDown}
                onBlur={handleInputBlur}
                minRows={minRows}
                maxLength={maxLength}
                className={cn(
                  "w-full text-base text-primary placeholder:text-tertiary p-2 bg-transparent border",
                  "border-border-border2",
                  "rounded-lg resize-none focus:outline-none focus:border-primary transition-[border-color] duration-200 ease-in-out",
                  "!border-border-border2 px-[3px] py-1",
                  variantClasses[inputVariant],
                  inputClassName
                )}
                aria-label="Edit text"
                aria-invalid={!!error}
                aria-describedby={error ? errorId : undefined}
              />
            ) : (
              <Input
                ref={inputRef}
                data-test={`${fieldName}-input`}
                value={getRawValue(value)}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={handleInputBlur}
                maxLength={maxLength}
                className={cn(
                  "p-2",
                  "w-full",
                  isNewFieldsUI &&
                    variant === "inline" &&
                    "bg-transparent focus:bg-transparent",
                  variantClasses[inputVariant],
                  inputClassName
                )}
                aria-label="Edit text"
                aria-invalid={!!error}
                aria-describedby={error ? errorId : undefined}
                style={{ width: "100%" }}
                containerClassName={cn(containerClassName, "w-full")}
                inlineElement={
                  showClearButton &&
                  !isEmpty(value) && (
                    <IconButton
                      size="xs"
                      variant="iconNoBg"
                      onClick={handleClearClick}
                      onMouseDown={(e) => e.preventDefault()}
                      aria-label="Clear value"
                      type="button"
                      className="mr-[-4px]"
                    >
                      <Close />
                    </IconButton>
                  )
                }
              />
            )}
          </div>
        ) : (
          renderEditButton()
        )}
        {error && !externalErrorId && (
          <Text id={errorId} role="alert" variant="smBodyError">
            {error}
          </Text>
        )}
      </div>
    );
  }
);

export default SingleInput;
